import { CognitoUserSession } from 'amazon-cognito-identity-js';

import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import logger from 'utils/logger';
import { withNetworkRetries } from 'utils/network/utils';

import { clearLocalAuthentication, getCognitoUserPool } from './cognito';
import { secureAuthStorage } from './secure-auth-storage';
import { getCognitoRetryConfig } from './util';

let pendingCurrentSessionCall: Promise<CognitoUserSession> | null = null;

const _getCurrentSessionWrapper = () => {
  // de-dupe calls to cognito as it does not dedupe internally for errors
  if (pendingCurrentSessionCall) {
    return pendingCurrentSessionCall;
  }
  const result = withNetworkRetries(
    async () => getSession(),
    getCognitoRetryConfig('currentSession')
  ).finally(() => {
    pendingCurrentSessionCall = null;
  });

  pendingCurrentSessionCall = result;

  return result;
};

export interface CurrentSessionResolution {
  session: CognitoUserSession | null;
  invalidatedPersistedSession: boolean;
}

// Return the current user session and whether a previously persisted identity was invalidated.
export const getCurrentSessionResolution = async (): Promise<CurrentSessionResolution> => {
  const hadPersistedSession = !!syncStorage.getString(SyncStorageKey.USER_ID);

  try {
    return {
      session: await _getCurrentSessionWrapper(),
      invalidatedPersistedSession: false,
    };
  } catch (e) {
    const isRefreshTokenExpired = /Refresh Token has expired/i.test(e?.toString() ?? '');
    if (isRefreshTokenExpired) {
      logger.info({
        errorContext: {
          name: (e as Error)?.name,
          code: (e as Error & { code?: string })?.code,
          message: (e as Error)?.message,
        },
        message: 'Refresh token expired, user logged out.',
      });
    } else {
      // try to debug if keys are malformed somehow which is causing these strange issues
      let authKeyLengthInfo: Record<string, number> | undefined;
      try {
        authKeyLengthInfo = Object.fromEntries(
          secureAuthStorage
            .getAllKeys()
            .filter(key => key.startsWith('CognitoIdentityServiceProvider'))
            .map(key => [
              key?.substring(key?.lastIndexOf('.') + 1),
              secureAuthStorage.getItem(key)?.length ?? 0,
            ])
        );
      } catch (_) {} // just in case as this could technically throw

      logger.error({
        error: e,
        message: 'An error occurred while getting the auth session.',
        authKeyLengthInfo,
      });
    }

    // if cognito failed to get a valid session
    // clear local storage to prevent a user with
    // no session from being "logged in" in app state
    clearLocalAuthentication();
    return {
      session: null,
      invalidatedPersistedSession: hadPersistedSession,
    };
  }
};

let pendingForceRefreshCall: Promise<CognitoUserSession> | null = null;

export const forceRefreshGetCurrentSession = () => {
  if (pendingForceRefreshCall) {
    return pendingForceRefreshCall;
  }

  const result = withNetworkRetries(async () => {
    await new Promise((resolve, reject) => {
      const currentUser = getCognitoUserPool()?.getCurrentUser();

      if (!currentUser) {
        return reject(new Error('No current user for userPool.'));
      }

      // First call getSession to populate this.signInUserSession from storage
      // This is required because getUserData checks this.signInUserSession
      // but doesn't load it from storage like getSession does
      currentUser.getSession((sessionErr: Error | null, session: CognitoUserSession | null) => {
        if (sessionErr || !session) {
          return reject(sessionErr || new Error('getSession failed'));
        }

        // Force refresh the session using the refresh token
        const refreshToken = session.getRefreshToken();
        if (!refreshToken || !refreshToken.getToken()) {
          return reject(new Error('No refresh token available, forceRefresh failed'));
        }

        currentUser.refreshSession(refreshToken, (refreshErr, refreshedSession) => {
          if (refreshErr || !refreshedSession) {
            return reject(refreshErr || new Error('Failed to refresh session'));
          }

          // Now call getUserData with the refreshed session
          currentUser.getUserData(
            (err, result) => {
              if (err || !result) {
                return reject(err || new Error('getUserData result is null'));
              }

              resolve(result);
            },
            { bypassCache: true }
          );
        });
      });
    });
    return getSession();
  }, getCognitoRetryConfig('forceRefreshGetCurrentSession'))
    .then(user => {
      const token = user.getIdToken();
      const jwt = token.getJwtToken();
      logger.info({
        message: 'forceRefreshGetCurrentSession succeeded',
        isForceRefreshUserSession: true,

        // TODO could remove these fields later after we gather data on this situation
        jwtLength: jwt.length,
        jwtExp: token.getExpiration(),
        jwtIat: token.getIssuedAt(),
      });
      return user;
    })
    .catch(error => {
      // soft log the user out setting them to null in memory, there is no recovery here and the UI can get into a very funky state otherwise
      // "soft" don't clear everything and behaves similar to cognito refresh token timeout (currently 1 year)
      logger.error({
        error,
        message: 'forceRefreshGetCurrentSession failed, soft clearing user',
        isForceRefreshUserSession: true,
      });
      return Promise.reject(error);
    })
    .finally(() => {
      pendingForceRefreshCall = null;
    });

  pendingForceRefreshCall = result;

  return result;
};

// internal ONLY call to get the session
const getSession = async () => {
  const cognitoUser = getCognitoUserPool().getCurrentUser();

  if (!cognitoUser) {
    throw new Error('No current user for userPool.');
  }

  const session = await new Promise<CognitoUserSession>((resolve, reject) =>
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || session == null) {
        return reject(err);
      }
      return resolve(session);
    })
  );

  if (!session) {
    throw new Error('Failed to getSession but no error thrown.');
  }

  return session;
};

// Return if there is an unexpected sign outs tracking
export const checkForUnexpectedSignOut = (): Promise<Error> =>
  _getCurrentSessionWrapper()
    .then(() => {
      // Add a marker when a user is successfully sign in to track unexpected sign outs
      syncStorage.setBoolean(SyncStorageKey.USER_SIGNED_IN_SUCCESSFULLY, true);
      return null;
    })
    .catch(error => {
      // First check if the user has been logged in successfully
      const userSignedInSuccessfully = syncStorage.getBoolean(
        SyncStorageKey.USER_SIGNED_IN_SUCCESSFULLY
      );
      if (userSignedInSuccessfully) {
        // Remove the marker and return that there has been an unexpected sign out
        syncStorage.remove(SyncStorageKey.USER_SIGNED_IN_SUCCESSFULLY);
        return error;
      }
      return null;
    });
